import React, {useContext, useState, useEffect } from "react";
import { Trans } from "react-i18next";
import { filter } from "lodash";
import { HolidayStatus } from "components";
import { PendingButton } from "components/common";
import { usePopover } from "hooks";
import { PopoverBody, Button, Card, CardBody } from "reactstrap";
import { Link } from "react-router-dom";
import EmployeeContext from "contexts/EmployeeContext";

const HolidayCancelButton = ({ holiday, onDelete }) => {
    const { Popover, toggle, setIsOpen } = usePopover();
    const [pending, setPending] = useState();
    const id = `holiday-cancel-${holiday.id}`;

    const onConfirm = async () => {
        setPending(true);
        setIsOpen(false);

        try {
            await onDelete(holiday);
        } finally {
            setPending(false);
        }
    };

    return (
        <div>
            <Popover target={`#${id}`} placement="bottom">
                <PopoverBody>
                    <p className="text-center">
                        <Trans>Are you sure you want to cancel this holiday request?</Trans>
                    </p>
                    <div className="d-flex justify-content-end">
                        <PendingButton
                            color="primary"
                            onClick={onConfirm}
                            size="sm"
                            pending={pending}
                        >
                            Confirm
                        </PendingButton>
                    </div>
                </PopoverBody>
            </Popover>
            <PendingButton
                className="mr-2"
                id={id}
                size="sm"
                color="danger"
                pending={pending}
                onClick={toggle}
            >
                Cancel
            </PendingButton>
        </div>
    );
};



const List = ({ client, limit = false, match }) => {
    const [holidays, setHolidays] = useState();
    const [viewAllHolidays, setViewAllHolidays] = useState(true);
    const [accrued, setAccrued] = useState(null);
    const { employee } = useContext(EmployeeContext);

    useEffect(
        () =>
            void (async () => {
                if (!employee) return;
                const holidays = await client
                    .get(`employees/${employee.id}/holiday_requests`)
                    .get("data");
                const upcoming = filter(
                    holidays,
                    (i) => !i.past && i.approved !== false
                );

                const accrued = await client
                .get(`employees/${employee.id}/accrued_holidays`)
                .get("data");

                setHolidays({
                    upcoming,
                    all: holidays,
                });
                setAccrued(accrued);
            })(),
        [employee]
    );

    async function onDelete(holiday) {
        await client.delete(
            `employees/${employee.id}/holiday_requests/${holiday.id}`
        );
        setHolidays({
            upcoming: filter(holidays.upcoming, (i) => i.id !== holiday.id),
            all: filter(holidays.all, (i) => i.id !== holiday.id),
        });
    }

    if (!holidays) return null;

    return (
        <div className="holidays">
            {accrued && <div className="mb-3 align-items-center justify-content-between">
                <Card className="shadow-sm border">
                    <CardBody>
                        <div className="row mb-3">
                            <div className="col-6">
                                <h5 className="d-print-none">
                                    <Trans>Time Offs Summary</Trans>
                                </h5>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-3 border-right">
                                <p> <Trans>Earned</Trans></p>
                                <h5>{accrued.earned}</h5>
                            </div>
                            <div className="col-3 border-right">
                                <p> <Trans>Used holidays</Trans></p>
                                <h5>{accrued.used}</h5>
                            </div>
                            <div className="col-3 border-right">
                                <p> <Trans>Balance</Trans></p>
                                <h5>{accrued.balance}</h5>
                            </div>
                            <div className="col-3">
                                <p> <Trans>Public Holidays</Trans></p>
                                <h5>{employee.num_holiday_days}</h5>
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </div>
            }
            <div className="d-print-none">
                <Card className="shadow-sm border">
                    <CardBody>
                        <div className="row mb-3">
                            <div className="col-6">
                                <h5 className="d-print-none">
                                    <Trans>Time Offs</Trans>
                                </h5>
                            </div>
                            <div className="col-6 text-right">
                                <Link
                                    className="btn btn-primary btn-sm d-print-none"
                                    to={`/employee_payroll/holidays/${employee.id}/request_holiday`} >
                                    <Trans>New Time Off Request</Trans>
                                </Link>
                            </div>
                        </div>

                        <div className="row">
                            <Button className="p-1 mb-2 btn btn-secondary" onClick={() => setViewAllHolidays(!viewAllHolidays)} >
                                <Trans>View {viewAllHolidays ? "Upcoming" : "All"} Time Off</Trans>
                            </Button>
                            <table className="table table-striped employee-holiday-table">
                                <thead>
                                    <tr>
                                        <th className="font-weight-semibold p-2">
                                            <Trans>From</Trans>
                                        </th>
                                        <th className="font-weight-semibold p-2">
                                            <Trans>To</Trans>
                                        </th>
                                        <th className="font-weight-semibold p-2">
                                            <Trans>Status</Trans>
                                        </th>
                                        <th className="font-weight-semibold p-2">
                                            <Trans>Note</Trans>
                                        </th>
                                        <th><Trans>Cancel</Trans></th>
                                    </tr>
                                </thead>
                                <tbody className="small">
                                    {holidays[viewAllHolidays ? "all" : "upcoming"].slice(0, limit || holidays[viewAllHolidays ? "all" : "upcoming"].length).map((i) => (
                                        <tr key={i.id}>
                                            <td className="p-2">
                                                {i.starting} <Trans>{i.starting_time}</Trans>
                                            </td>
                                            <td className="p-2">
                                                {i.ending} <Trans>{i.ending_time}</Trans>
                                            </td>
                                            <td className="p-2">
                                                <HolidayStatus approved={i.approved} />
                                            </td>
                                            <td className="p-2">
                                                {i.admin_reason}
                                            </td>
                                            <td className="p-2">
                                                {i.approved !== false && i.upcoming && (
                                                    <HolidayCancelButton holiday={i} onDelete={onDelete} />
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardBody>
                </Card>
            </div>
        </div>
    );
};

export default List